import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import {
  Music,
  Trash2,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  Radio,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function DashboardAutoDJ() {
  const { user } = useAuth();
  const [station, setStation] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload form
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [uploadError, setUploadError] = useState("");

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      // Get user's station
      const stQ = query(collection(db, "stations"), where("creatorId", "==", user.uid));
      const stSnap = await getDocs(stQ);
      if (stSnap.empty) { setLoading(false); return; }

      const st = { id: stSnap.docs[0].id, ...stSnap.docs[0].data() };
      setStation(st);

      // Load autodj tracks subcollection
      const djQ = query(
        collection(db, "stations", st.id, "autodj"),
        // orderBy is fine without index for subcollections
      );
      const djSnap = await getDocs(djQ);
      setTracks(djSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!file || !title.trim() || !station) return;
    setUploading(true);
    setUploadError("");
    setStatusMsg("Uploading & converting...");
    try {
      const idToken = await user.getIdToken(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("track_title", title.trim());
      formData.append("show_id", "");

      const res = await fetch(`${API_URL}/api/audio/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");

      // Write to autodj subcollection
      const newTrack = {
        title: data.metadata.title || title,
        artist: data.metadata.artist || "",
        mp3FileUrl: data.downloadURL,
        durationSeconds: data.metadata.durationSeconds || 0,
        createdAt: serverTimestamp(),
      };
      const djRef = await addDoc(
        collection(db, "stations", station.id, "autodj"),
        newTrack
      );

      setTracks((t) => [...t, { id: djRef.id, ...newTrack }]);
      setStatusMsg("Added!");
      setFile(null);
      setTitle("");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch (err) {
      setUploadError(err.message);
      setStatusMsg("");
    }
    setUploading(false);
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm("Remove this track from Auto-DJ?")) return;
    await deleteDoc(doc(db, "stations", station.id, "autodj", trackId));
    setTracks((t) => t.filter((x) => x.id !== trackId));
  };

  const totalRuntime = tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-6 h-6 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!station) {
    return (
      <div data-testid="autodj-no-station" className="text-center py-16 space-y-3">
        <Radio size={40} className="mx-auto text-zinc-700" />
        <h2 className="text-white font-bold text-base">No Station Yet</h2>
        <p className="text-zinc-500 text-sm">Create your station first, then add Auto-DJ tracks.</p>
        <Link
          to="/dashboard/station"
          className="inline-flex items-center gap-2 bg-[#B026FF] text-white font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#9b1ee6] transition-colors"
        >
          Set Up Station
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-autodj" className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Auto-DJ Tracks</h2>
          <p className="text-zinc-500 text-xs mt-0.5">
            These tracks fill empty schedule slots for <span className="text-white">{station.name}</span>.
          </p>
        </div>
        {tracks.length > 0 && (
          <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
            <Clock size={12} />
            <span>Total: {formatTime(totalRuntime)}</span>
          </div>
        )}
      </div>

      {/* Upload card */}
      <div className="bg-[#111] border border-[#B026FF]/20 rounded-2xl p-5 space-y-3">
        <h3 className="text-white font-semibold text-sm">Add a Track</h3>
        <input
          data-testid="autodj-title-input"
          type="text"
          placeholder="Track title"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setUploadError(""); }}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50"
        />
        <div className="flex items-center gap-2">
          <label
            data-testid="autodj-file-label"
            className="flex-1 border border-dashed border-white/10 rounded-xl px-3 py-2 text-xs text-zinc-500 cursor-pointer hover:border-[#B026FF]/30 transition-colors truncate"
          >
            {file ? file.name : "Select audio (MP3, WAV, FLAC, OGG, AAC, M4A)"}
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => { if (e.target.files[0]) { setFile(e.target.files[0]); setUploadError(""); } }}
            />
          </label>
          <button
            data-testid="autodj-upload-btn"
            onClick={handleUpload}
            disabled={!file || !title.trim() || uploading}
            className="flex items-center gap-1.5 bg-[#B026FF] text-white font-bold text-xs px-4 py-2 rounded-xl hover:bg-[#9b1ee6] transition-colors disabled:opacity-50 min-w-[70px] justify-center"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "..." : "Add"}
          </button>
        </div>
        {statusMsg && !uploading && (
          <p className="text-[#00ff77] text-xs flex items-center gap-1.5">
            <CheckCircle size={10} /> {statusMsg}
          </p>
        )}
        {uploading && (
          <p className="text-zinc-500 text-xs flex items-center gap-1.5">
            <Loader2 size={10} className="animate-spin" /> {statusMsg}
          </p>
        )}
        {uploadError && (
          <p className="text-red-400 text-xs flex items-center gap-1.5">
            <AlertCircle size={10} /> {uploadError}
          </p>
        )}
      </div>

      {/* Track list */}
      <div className="space-y-2">
        {tracks.length === 0 ? (
          <div className="text-center py-12 text-zinc-600">
            <Music size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No Auto-DJ tracks yet.</p>
            <p className="text-xs mt-1">Add tracks above to fill empty schedule slots.</p>
          </div>
        ) : (
          tracks.map((track, i) => (
            <div
              key={track.id}
              data-testid={`autodj-track-${track.id}`}
              className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl px-4 py-3"
            >
              <span className="text-zinc-600 text-xs w-5 text-right flex-shrink-0">{i + 1}</span>
              <Music size={13} className="text-zinc-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{track.title}</p>
                {track.artist && <p className="text-zinc-500 text-xs truncate">{track.artist}</p>}
              </div>
              <span className="text-zinc-500 text-xs flex-shrink-0">{formatTime(track.durationSeconds)}</span>
              <button
                onClick={() => handleDelete(track.id)}
                data-testid={`delete-autodj-${track.id}`}
                className="text-zinc-600 hover:text-red-400 transition-colors p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      {!station.autodjEnabled && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-xs text-yellow-400">
          Auto-DJ is currently disabled for your station.{" "}
          <Link to="/dashboard/station" className="underline">Enable it in Station settings</Link>.
        </div>
      )}
    </div>
  );
}
